import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { DenounceAdminService } from '../../../services/denounce-admin.service';
import { DenounceAccessService } from '../../../services/denounce-access.service';
import { DenouncePublicApiService } from 'src/app/denounce-public/services/denounce-public-api.service';
import {
  Denounce,
  DenounceFilters,
  DenounceOperator,
  DenounceStatus,
  DENOUNCE_STATUS_LABELS,
  DENOUNCE_STATUS_COLORS,
} from '../../../interfaces/denounce.interface';

@Component({
  selector: 'app-denounce-list',
  templateUrl: './denounce-list.component.html',
  styleUrls: ['./denounce-list.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DenounceListComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  loading = false;
  activeTab: 'pending' | 'history' = 'pending';

  displayedColumns = [
    'trackingId',
    'category',
    'relationship',
    'accusedName',
    'status',
    'assignedOperator',
    'createdAt',
    'actions',
  ];

  dataSource = new MatTableDataSource<Denounce>([]);
  total = 0;
  pageSize = 10;
  currentPage = 1;

  searchText = '';

  // Filtros avanzados
  showAdvancedFilters = false;
  filterStatus = '';
  filterCategory = '';
  filterOperator = '';
  filterDateFrom = '';
  filterDateTo = '';
  filterUnassignedOnly = false;

  categories: { value: string; label: string }[] = [];
  operators: DenounceOperator[] = [];

  statusLabels = DENOUNCE_STATUS_LABELS;
  statusColors = DENOUNCE_STATUS_COLORS;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private denounceService: DenounceAdminService,
    private accessService: DenounceAccessService,
    private publicService: DenouncePublicApiService,
    private router: Router,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.denounceService.loading$.subscribe((l) => {
        this.loading = l;
        this.cdr.markForCheck();
      }),
    );
    this.loadCategories();
    this.loadOperators();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadCategories(): void {
    this.publicService.getConfig().subscribe({
      next: (cfg) => {
        this.categories = cfg.categories;
        this.cdr.markForCheck();
      },
    });
  }

  loadOperators(): void {
    this.accessService.listOperators().subscribe({
      next: (ops) => {
        this.operators = ops.filter((o) => o.isActive);
        this.cdr.markForCheck();
      },
    });
  }

  onTabChange(tab: 'pending' | 'history'): void {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterStatus = '';
    this.loadData();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadData();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterCategory = '';
    this.filterOperator = '';
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.filterUnassignedOnly = false;
    this.searchText = '';
    this.currentPage = 1;
    this.loadData();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filterStatus ||
      this.filterCategory ||
      this.filterOperator ||
      this.filterDateFrom ||
      this.filterDateTo ||
      this.filterUnassignedOnly ||
      this.searchText
    );
  }

  loadData(): void {
    const filters: DenounceFilters = {
      search: this.searchText || undefined,
      category: this.filterCategory || undefined,
      assignedTo: this.filterOperator || undefined,
      dateFrom: this.filterDateFrom || undefined,
      dateTo: this.filterDateTo || undefined,
      status: this.filterStatus || undefined,
      page: this.currentPage,
      limit: this.pageSize,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    };

    this.denounceService.getList(filters).subscribe({
      next: (res) => {
        let data = res.data;

        // Tabs: filtrar por estado pendiente/cerrado si NO hay filterStatus explícito
        if (!this.filterStatus) {
          if (this.activeTab === 'pending') {
            data = data.filter(
              (d) => d.status === 'RECIBIDA' || d.status === 'EN_INVESTIGACION',
            );
          } else {
            data = data.filter(
              (d) => d.status === 'CONCLUIDA' || d.status === 'DESESTIMADA',
            );
          }
        }

        // Filtro sin asignar (client-side, el back no lo soporta con un flag)
        if (this.filterUnassignedOnly) {
          data = data.filter((d) => !d.assignedTo);
        }

        this.dataSource.data = data;
        this.total = res.total;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.snackBar.open(err.message || 'Error al cargar denuncias', 'Cerrar', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar'],
        });
        this.cdr.markForCheck();
      },
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  goToDetail(denounce: Denounce): void {
    this.router.navigate(['/wallet/admin/denounces', denounce.id]);
  }

  getStatusLabel(status: DenounceStatus): string {
    return this.statusLabels[status] || status;
  }

  getStatusColor(status: DenounceStatus): string {
    return this.statusColors[status] || '#757575';
  }

  getCategoryLabel(value: string): string {
    return this.categories.find((c) => c.value === value)?.label || value;
  }

  daysSinceCreated(createdAt: string): number {
    const created = new Date(createdAt).getTime();
    return Math.floor((Date.now() - created) / (1000 * 60 * 60 * 24));
  }

  isStale(d: Denounce): boolean {
    return (
      !d.assignedTo &&
      (d.status === 'RECIBIDA' || d.status === 'EN_INVESTIGACION') &&
      this.daysSinceCreated(d.createdAt) >= 7
    );
  }

  exportCsv(): void {
    const filters: DenounceFilters = {
      search: this.searchText || undefined,
      category: this.filterCategory || undefined,
      assignedTo: this.filterOperator || undefined,
      dateFrom: this.filterDateFrom || undefined,
      dateTo: this.filterDateTo || undefined,
      status: this.filterStatus || undefined,
    };
    this.denounceService.exportCsv(filters).subscribe({
      next: (res) => {
        const blob = new Blob([res.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `denuncias_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.snackBar.open('Error al exportar', 'Cerrar', { duration: 3000 });
      },
    });
  }
}
